<template>
	<main class="mypage login">
		<article class="login_Info">
			<section class="login_form">
				<h6>로그인</h6>
				<div class="c_fieldArea">
					<FieldLayout title="아이디">
						<InputText v-model="id" :placeholder="INPUT_ID" @keydown.enter="onClickLogin" />
					</FieldLayout>
					<FieldLayout title="비밀번호">
						<InputPasswordLock v-model="password" show-toggle :placeholder="INPUT_PW" @keydown.enter="onClickLogin" />
					</FieldLayout>
					<div class="button_box">
						<button
							class="c_button radius-x-small primary-fill save"
							:disabled="!id || !password"
							@click="onClickLogin"
						>
							로그인
						</button>
						<div class="terms">
							<router-link :to="{ name: 'auth/find-id' }">아이디 찾기</router-link>
							<router-link to="/auth/find-pw">비밀번호 찾기</router-link>
							<router-link :to="{ name: 'auth/terms-agreement', query: signUpQuery }">회원가입</router-link>
						</div>
					</div>
				</div>
				<div class="social_login">
					<img src="@/assets/img/login/kakao.png" alt="카카오 아이콘" loading="lazy" @click="onRequestKakaoLogin" />
					<img src="@/assets/img/login/google.png" alt="구글 아이콘" loading="lazy" @click="onRequestGoogleLogin" />
					<img src="@/assets/img/login/apple.png" alt="애플 아이콘" loading="lazy" @click="onRequestAppleLogin" />
				</div>
			</section>
		</article>
	</main>
</template>

<script setup lang="ts">
// core
import { computed, nextTick, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { until } from '@vueuse/core';
// lib
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';

// api
import { useLoginApi, useRequestTSignApi } from '@/api/auth';
import { useGetMasterListApi } from '@/api/master';
// component
import InputPasswordLock from '@/components/atom/input/InputPasswordLock.vue';
import InputText from '@/components/atom/input/InputText.vue';
import FieldLayout from '@/components/form/FieldLayout.vue';
// constant
import {
	COMPANY_TITLE,
	ERROR_ID_PASSWORD_MISMATCH,
	ERROR_MEMBER_REGISTRATION_REQUIRED,
	ERROR_OCCURRED,
	INPUT_ID,
	INPUT_PW,
} from '@/constants/title';
import { useAuthStore } from '@/stores/auth';
import { useDeviceStore } from '@/stores/device';
import { useMobileDeviceStore } from '@/stores/mobileDevice';
// store
import { usePopupStore } from '@/stores/popup';
import { useTokenStore } from '@/stores/token';
import { getPushToken } from '@/utils/directive/composables/usePushToken';
// util
import { deviceTypeNative } from '@/utils/function/deviceTypeNative';
import { socialLoginApple, socialLoginGoogle } from '@/utils/function/socialUtils';

// -----variable-----
const route = useRoute();
const router = useRouter();
const popupStore = usePopupStore();
const tokenStore = useTokenStore();
const authStore = useAuthStore();
const deviceStore = useDeviceStore();
const mobileDeviceStore = useMobileDeviceStore();

const id = ref('');
const password = ref('');
const receivedToken = computed(() => {
	return route.query.access_token ? String(route.query.access_token) : '';
});
const loginRedirectPath = computed(() => {
	const redirect = route.query.redirect || window.localStorage.getItem('loginRedirect');
	if (typeof redirect !== 'string' || !redirect.startsWith('/') || redirect.startsWith('//')) {
		return '';
	}
	return redirect;
});
const signUpQuery = computed(() => {
	const query: {
		joinPath: string;
		redirect?: string;
	} = {
		joinPath: 'normal',
	};
	if (loginRedirectPath.value) {
		query.redirect = loginRedirectPath.value;
	}
	return query;
});

// -----api-----
const { mutateAsync: login } = useLoginApi();
const { mutateAsync: requestTSign } = useRequestTSignApi();
const { data: masterListData } = useGetMasterListApi(computed(() => tokenStore.isLogin));
// -----function-----
const randomMasterImg = async () => {
	await nextTick();
	await until(() => masterListData.value).toBeTruthy();

	// master_list_onsub과 master_list_nosub가 존재하는지 확인
	if (!masterListData.value?.master_list_onsub || !masterListData.value?.master_list_nosub) {
		return null;
	}
	// file_path 배열 만들기
	const file_path = [
		...masterListData.value.master_list_onsub.map((item) => item.file_path),
		...masterListData.value.master_list_nosub.map((item) => item.file_path),
	];
	// 파일이 없을 경우 기본 이미지 반환
	if (file_path.length === 0) {
		return null;
	}
	// 랜덤 이미지 선택
	const randomNum = Math.floor(Math.random() * file_path.length);
	return file_path[randomNum];
};

const onClickLogin = throttle(async () => {
	if (!(id.value && password.value)) {
		return;
	}
	const params: {
		id: string;
		password: string;
		device_type?: string;
	} = {
		id: id.value,
		password: password.value,
	};
	if (deviceStore.getDevice === 'MO') {
		params.device_type = mobileDeviceStore.getMobileDevice;
	}
	const res = await login(params);
	if (res.data.success) {
		authStore.setAuth({ member_nickname: res.data.data.member_nickname });
		authStore.setId(id.value);
		authStore.setUserType({
			group_pk: res.data.data.group_pk,
			group_sp: res.data.data.group_sp,
		});
		const img = await randomMasterImg();
		authStore.setUserImg(img ?? '');
		getPushToken();
		if (loginRedirectPath.value) {
			window.localStorage.removeItem('loginRedirect');
			router.replace(loginRedirectPath.value);
			return;
		}
		if (!route.query.isPayment) {
			router.replace('/');
		} else {
			router.push('/master-info');
		}
	} else {
		popupStore.openPopup({
			content: COMPANY_TITLE,
			subtext: ERROR_ID_PASSWORD_MISMATCH,
		});
	}
}, 500);

// 소셜로그인 관련 START
const setLoginRedirectStorage = () => {
	if (loginRedirectPath.value) {
		window.localStorage.setItem('loginRedirect', loginRedirectPath.value);
	}
};

// 카카오 로그인
const onRequestKakaoLogin = debounce(() => {
	setLoginRedirectStorage();
	onRequestKakaoSignin();
	window.localStorage.setItem('joinPath', 'kakao');
	// currentClickedSocial.value = 'kakao';
	deviceTypeNative();
}, 300);
const onRequestKakaoSignin = () => {
	socialOpen(`${import.meta.env.VITE_SOCIAL_API_URL}/api/kakao/signin`);
	// setIsSocialLoading(false);
};

// 카카오 로그인 페이지 새 창 호출
function socialOpen(url: string) {
	window.location.href = url;
}

// 애플 로그인
const onRequestAppleLogin = debounce(() => {
	setLoginRedirectStorage();
	socialLoginApple();
	window.localStorage.setItem('joinPath', 'apple');
	// currentClickedSocial.value = 'apple';
	deviceTypeNative();
}, 300);

// 구글 로그인
const onRequestGoogleLogin = debounce(() => {
	setLoginRedirectStorage();
	socialLoginGoogle();
	window.localStorage.setItem('joinPath', 'google');
	window.localStorage.setItem('socialAccessToken', String(route.query.access_token));
	// currentClickedSocial.value = 'google';
	deviceTypeNative();
}, 300);

// 소셜 로그인 - 기본 정보 체크
const checkSocialToken = () => {
	if (receivedToken.value) {
		const token = String(receivedToken.value);
		tokenStore.setAccessTokenForSNSSignUp(token);
		const params: {
			token: string;
			device_type?: string;
		} = {
			token: token,
		};
		if (deviceStore.getDevice === 'MO') {
			params.device_type = mobileDeviceStore.getMobileDevice;
		}
		requestTSign(params)
			.then((res) => {
				const {
					access_token,
					refresh_token,
					basic_info_yn,
					member_name,
					member_nickname,
					member_join_path,
					group_pk,
					group_sp,
				} = res.data.data;
				tokenStore.setToken(access_token, refresh_token);
				authStore.setUserInfo({
					member_name: member_name,
					member_nickname: member_nickname,
					member_join_path: member_join_path,
				});
				authStore.setUserType({
					group_pk: group_pk,
					group_sp: group_sp,
				});
				getPushToken();
				//가입한 이력이 없는 경우 회원가입으로 이동
				if (basic_info_yn === false) {
					return popupStore.openPopup({
						content: COMPANY_TITLE,
						subtext: ERROR_MEMBER_REGISTRATION_REQUIRED,
						confirm: () =>
							router.push({
								name: 'auth/terms-agreement',
								query: {
									joinPath: window.localStorage.getItem('joinPath'),
									...(loginRedirectPath.value ? { redirect: loginRedirectPath.value } : {}),
								},
							}),
						cancel: () =>
							router.push({
								name: 'auth/login',
							}),
					});
				}
				if (loginRedirectPath.value) {
					window.localStorage.removeItem('loginRedirect');
					router.replace(loginRedirectPath.value);
					return;
				}
				if (!route.query.isPayment) {
					router.replace('/');
				} else {
					router.push('/master-info');
				}
			})
			.catch((msg) => {
				console.log(msg);
			});
	} else if (receivedToken.value == 'false') {
		alert(ERROR_OCCURRED);
	}
};

watch(
	() => tokenStore.isLogin,
	async () => {
		// 소셜 로그인 토큰 확인
		checkSocialToken();
	},
	{
		immediate: true,
	}
);
</script>

<style lang="scss" scoped>
.login {
	max-width: 40rem;
	padding-left: 0rem;
	padding-right: 0rem;
	.login_Info {
		width: 100%;
		padding-bottom: 19.3rem;
		.login_form {
			display: flex;
			flex-direction: column;
			gap: var(--space-xxx-large);
			.c_fieldArea {
				.button_box {
					display: flex;
					flex-direction: column;
					gap: var(--space-x-large);
					.c_button {
						width: 100%;
						height: 4.8rem;
					}
					.terms {
						display: flex;
						align-items: center;
						justify-content: center;
						flex-wrap: wrap;
						flex-direction: row;
						position: relative;
						// gap: 1.6rem;
						color: var(--color-font-lighter);
						font-weight: var(--font-w-medium);
						font-size: var(--font-s-mid);
						line-height: 1.551rem;

						:nth-child(1),
						:nth-child(2) {
							padding-right: var(--space-x-small);
						}
						:nth-child(2),
						:nth-child(3) {
							padding-left: var(--space-x-small);
						}
					}
					.terms :nth-child(1)::after,
					.terms :nth-child(2)::after {
						content: '';
						display: inline-block;
						height: 50%; /* 원하는 높이로 조절 */
						border-right: 1px solid var(--color-base-mid-dark);
						position: absolute;
						top: 25%; /* border가 텍스트 중앙에 위치하도록 조절 */
						padding-right: var(--space-x-small);
					}
				}
			}
			.social_login {
				display: flex;
				justify-content: center;
				gap: 4.8rem;
				margin-top: 0.8rem;
				img {
					width: 4.8rem;
					height: auto;
					cursor: pointer;
				}
			}
		}
		@media screen and (max-width: 1280px) {
			padding: 0 2rem;
		}
	}
}
</style>
